import Component, { ComponentLoader } from '@bythepixel/component-loader';
import InViewSrc from '@Core/components/inViewSrc';
import InViewSrcset from '@Core/components/inViewSrcset';
import { EVENT_NAMES as ModalEvents } from '@Core/components/microModalAdpt/events';
import VehicleCard from '@Core/components/vehicleCard';
import { eventThrottle, setCanonicalUrl } from '@Core/utility';
import ModalTriggers from '@Front/modules/pages/facetedSearch/modalTriggers';
import StickyTriggers from '@Front/modules/pages/facetedSearch/stickyTriggers';
import MicroModalAdaptor from '@Core/components/microModalAdpt';

export default class FacetedSearch extends Component {
  static selector = '[data-comp-faceted-search]';
  static loaderPriority = 'in-view';

  constructor(element) {
    super(element);

    const modalTriggerElement = this.$container.querySelector(
      '[data-comp-faceted-search-modal-triggers]',
    );
    const stickyTriggerElement = this.$container.querySelector(
      '[data-comp-faceted-search-sticky-triggers]',
    );

    this.modalTriggers = new ModalTriggers(modalTriggerElement);
    this.stickyTriggers = new StickyTriggers(stickyTriggerElement);

    this.modalTriggers.events();
    this.stickyTriggers.events();

    this.globalStorePage = this.$container.dataset.globalStorePage;

    setCanonicalUrl(window.location.href);

    window.addEventListener(ModalEvents.OPENED, (e) => {
      const modalId = e.detail;

      if (modalId === 'filters-modal') {
        stickyTriggerElement.classList.add(
          'faceted-search-sticky-triggers--opened',
        );
      }
    });

    window.addEventListener(ModalEvents.CLOSED, (e) => {
      const modalId = e.detail;

      if (modalId === 'filters-modal') {
        stickyTriggerElement.classList.remove(
          'faceted-search-sticky-triggers--opened',
        );
      }
    });

    window.addEventListener(
      'resize',
      eventThrottle(() => {
        this.modalTriggers.events();
        this.stickyTriggers.events();
      }),
    );

    /**
     * Handles query parameter events dispatched from Faceted Search Livewire class
     */
    window.addEventListener('updateQueryParametersJs', (e) => {
      const { data } = e.detail;
      const { pathname } = window.location;

      // Stores parameters into an array for easier formatting
      const parameters = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, property] of Object.entries(data)) {
        if (property.value !== '') {
          parameters.push(`${property.type}=${property.value}`);
        }
      }

      // If there are parameters, this joins the parameters array and formats it as a query string
      if (parameters.length !== 0) {
        const queryString = `?${parameters.join('&')}`;
        window.history.replaceState(
          null,
          '',
          `${this.globalStorePage}${queryString}`,
        );
      } else {
        // Otherwise, route to global sore page
        window.history.replaceState(null, '', this.globalStorePage);
      }
    });

    window.addEventListener('scrollToTopJs', () => window.scrollTo(0, 0));

    /**
     * Reloads scripts when Livewire dispatches renderImages
     */
    window.addEventListener('faceted-search:rendered', () => {
      setCanonicalUrl(window.location.href);

      setTimeout(() => {
        this.reloadScripts();
        // Reinitialize promotion labels manager for new vehicle cards
        if (window.promotionLabelsManager) {
          window.promotionLabelsManager.findAndObserveContainers();
        }
      }, 100);
    });

    /**
     * Reloads scripts when browser state changes
     */
    window.addEventListener('popstate', () => this.reloadScripts());

    /**
     * Handles make filter collapsible in mobile
     */
    const makeFilterCollapsible = this.$container.querySelector(
      '[data-comp-faceted-search-collapsible-text="makes"]',
    );
    window.addEventListener('makeFilterCollapsibleJs', () => {
      // Do nothing in desktop
      if (window.matchMedia('(min-width: 768px)').matches) {
        return;
      }

      // Make filters
      const filters = makeFilterCollapsible.querySelectorAll(
        '.faceted-search-input-group',
      );

      // Hidden make filters
      const hiddenFilters = makeFilterCollapsible.querySelectorAll(
        '.faceted-search-input-group--hide',
      );

      // Collapsible text elements
      const collapsibleTextComponent = makeFilterCollapsible.querySelector(
        '[data-comp-collapsible-text]',
      );
      const collapsibleTextContainer = collapsibleTextComponent.querySelector(
        '[data-comp-collapsible-text-content]',
      );
      const toggle = collapsibleTextComponent.querySelector(
        '[data-comp-collapsible-text-toggle]',
      );

      // Check if collapsible text is expanded
      const isExpanded = collapsibleTextContainer.classList.contains(
        'max-lines__expanded',
      );

      // If collapsible text is not expanded, re-generate toggle text
      if (!isExpanded) {
        const total = filters.length - 4;
        const hidden = hiddenFilters.length;
        const difference = total - hidden;

        // Toggle visibility
        toggle.style.display = difference <= 0 ? 'none' : 'block';

        // Regenerate toggle text
        const text = `Show ${difference} more`;
        collapsibleTextComponent.dataset.expandText = text;
        toggle.innerText = text;
      }
    });

    /**
     * Resets heading when user interacts with faceted search
     */
    this.heading = document.querySelector('[data-comp-heading]');
    this.originalHeading = this.heading.dataset.compHeading;
    window.addEventListener('resetHeadingJs', () => {
      // Reset heading
      try {
        this.heading.innerText = this.originalHeading;
      } catch (e) {
        console.warn(e);
      }
    });

    /**
     * Resets breadcrumbs when user interacts with faceted search
     */
    this.breadcrumbs = document.querySelectorAll('[data-comp-breadcrumb]');
    window.addEventListener('resetBreadcrumbsJs', () => {
      if (this.breadcrumbs.length <= 1) {
        return;
      }

      // Hide middle breadcrumb
      try {
        this.breadcrumbs[this.breadcrumbs.length - 1].classList.add(
          'breadcrumbs__link--hide',
        );
      } catch (e) {
        console.warn(e);
      }
    });
  }

  reloadScripts() {
    if (this.$container.querySelector('[data-comp-faceted-search-products]')) {
      return new ComponentLoader(
        this.$container.querySelector('[data-comp-faceted-search-products]'),
        [InViewSrc, InViewSrcset, MicroModalAdaptor, VehicleCard],
      );
    }

    return new ComponentLoader(
      this.$container.querySelector('[data-comp-faceted-search-parts]'),
      [InViewSrc, InViewSrcset, MicroModalAdaptor],
    );
  }
}
